<template>
  <div class="kb-field" :class="{ 'kb-field--error': !!error, 'kb-field--disabled': disabled }">
    <div class="kb-field__wrap">
      <input
        v-if="!textarea"
        ref="controlRef"
        class="kb-field__control"
        :id="id"
        :type="computedType"
        :value="modelValue ?? ''"
        :placeholder="effectivePlaceholder"
        :disabled="disabled"
        :required="required"
        :autocomplete="autocomplete"
        :inputmode="inputmode"
        :maxlength="maxlength"
        :dir="dir"
        @input="$emit('update:modelValue', $event.target.value)"
        @keydown="$emit('keydown', $event)"
        @blur="onBlur"
        @focus="onFocus"
        @change="$emit('change', $event)"
      />

      <textarea
        v-else
        ref="controlRef"
        class="kb-field__control kb-field__control--textarea"
        :id="id"
        :value="modelValue ?? ''"
        :placeholder="effectivePlaceholder"
        :disabled="disabled"
        :required="required"
        :maxlength="maxlength"
        :rows="rows"
        :dir="dir"
        @input="$emit('update:modelValue', $event.target.value)"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
      />

      <label class="kb-field__label" :for="id">
        {{ label }}
        <span v-if="required" class="kb-field__req">*</span>
      </label>

      <div
        v-if="isPassword"
        class="kb-field__icon kb-field__icon--clickable"
        @click="togglePassword"
        role="button"
        aria-label="نمایش یا مخفی کردن رمز عبور"
      >
        <svg v-if="showPassword" width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path
            d="M1.5 12s4-7.5 10.5-7.5S22.5 12 22.5 12s-4 7.5-10.5 7.5S1.5 12 1.5 12Z"
            stroke="currentColor"
            stroke-width="1.8"
          />
          <circle cx="12" cy="12" r="3.5" stroke="currentColor" stroke-width="1.8" />
        </svg>

        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M3 3l18 18" stroke="currentColor" stroke-width="1.8" />
          <path d="M10.6 10.6A3.5 3.5 0 0 0 13.4 13.4" stroke="currentColor" stroke-width="1.8" />
          <path
            d="M2 12s4-7.5 10-7.5c2.1 0 4 .6 5.6 1.5M22 12s-1.5 2.8-4.1 4.9c-1.7 1.4-3.8 2.6-5.9 2.6-6 0-10-7.5-10-7.5"
            stroke="currentColor"
            stroke-width="1.8"
          />
        </svg>
      </div>

      <div v-if="$slots.rightIcon" class="kb-field__icon" aria-hidden="true">
        <slot name="rightIcon" />
      </div>
    </div>

    <div v-if="hint && !error" class="kb-field__hint">{{ hint }}</div>
    <div v-if="error" class="kb-field__error">{{ error }}</div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";

const props = defineProps({
  modelValue: [String, Number],
  label: { type: String, required: true },
  id: { type: String, default: () => `kbfi_${Math.random().toString(16).slice(2)}` },
  type: { type: String, default: "text" },
  placeholder: { type: String, default: "" },
  showPlaceholder: { type: Boolean, default: true },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  error: { type: String, default: "" },
  hint: { type: String, default: "" },
  maxlength: [String, Number],
  autocomplete: { type: String, default: "off" },
  inputmode: { type: String, default: "" },
  textarea: { type: Boolean, default: false },
  rows: { type: Number, default: 2 },
  dir: { type: String, default: "rtl" },
});

const emit = defineEmits(["update:modelValue", "keydown", "blur", "focus", "change"]);

const showPassword = ref(false);
const controlRef = ref(null);
const isPassword = computed(() => props.type === "password");
const computedType = computed(() => {
  if (!isPassword.value) return props.type;
  return showPassword.value ? "text" : "password";
});

const effectivePlaceholder = computed(() => " ");

function togglePassword() {
  showPassword.value = !showPassword.value;
}

function onFocus(e) {
  emit("focus", e);
}

function onBlur(e) {
  emit("blur", e);
}

defineExpose({
  focus: () => controlRef.value?.focus(),
  blur: () => controlRef.value?.blur(),
});
</script>
